import { useCallback, useEffect, useState } from "react";
import type { LeadTransferCandidate } from "@/lib/lead/transferLeadOwner";
import { fetchLeadTransferCandidates } from "@/lib/lead/transferLeadOwner";
import {
  createOpsDeal, createOpsTask, getOpsDeal, getOpsOwnershipDigestPreview, getOpsDealUploadStub, getOpsSettings,
  getOpsSummary, listOpsDeals, listOpsFastfieldSubmissions, listOpsIntegrationEvents, listOpsNotifications, listOpsTasks, markAllOpsNotificationsRead,
  patchOpsPaperworkItem, patchOpsSettings, patchOpsTask, postOpsDealSalesperson, postOpsDealTypeMatch, postOpsFastfieldSync, rotateOpsInventoryHostWebhookToken, seedOpsPaperworkRuleSet, seedOpsTemplates,
} from "@/lib/opsPaperworkClient";
import type { FastfieldSubmissionLedgerRow } from "@/lib/opsPaperworkClient";
import type { DealDetail, DealRow, IntEvent, OrgTask, PaperItem } from "./opsPaperworkTypes";
import type { DealTypeMatchCatalog, DealTypeMatchState } from "@/lib/opsDealTypes/dealTypeMatch";
import { DEALS_PICKER_PAGE_SIZE } from "@/lib/ops/opsDealsListQuery";
import type { OpsPaperworkAttachment } from "@/lib/opsPaperworkAttachmentsClient";
import { consumePendingOpsPaperworkDealId } from "@/lib/opsLookup/openOpsPaperworkDeal";
import { inviteDealSalesperson } from "./OpsDealSalespersonBanner";
import { opsPaperworkTabAllowed, visibleOpsPaperworkTabs, type OpsPaperworkTab } from "@/lib/ops/opsPaperworkTabs";

export function useOpsPaperworkPanelModel(args: { auth: any; scopedOrgId: string | null; canManageOps: boolean; canReviewDeals: boolean; uid: string }) {
  const { scopedOrgId, canManageOps, canReviewDeals } = args;
  const canOpenDeals = canManageOps || canReviewDeals;
  const [tab, setTab] = useState<OpsPaperworkTab>(canOpenDeals ? "deals" : "tasks");
  const [summary, setSummary] = useState<{ unread: number; overdue: number; tasks: number; unmappedLots: number } | null>(null);
  const [deals, setDeals] = useState<DealRow[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<DealDetail | null>(null);
  const [events, setEvents] = useState<IntEvent[]>([]);
  const [tasksInbox, setTasksInbox] = useState<OrgTask[]>([]);
  const [tasksSent, setTasksSent] = useState<OrgTask[]>([]);
  const [notifications, setNotifications] = useState<{ id: string; title: string; body: string; read_at: string | null; created_at: string; deal_id: string | null }[]>([]);
  const [settings, setSettings] = useState<Record<string, unknown> | null>(null);
  const [fastfieldSubmissions, setFastfieldSubmissions] = useState<FastfieldSubmissionLedgerRow[]>([]);
  const [fastfieldLoading, setFastfieldLoading] = useState(false);
  const [webhookTokenBusy, setWebhookTokenBusy] = useState(false);
  const [salespersonBusy, setSalespersonBusy] = useState(false);
  const [typeMatchBusy, setTypeMatchBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [newDealName, setNewDealName] = useState("");
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskAssignee, setNewTaskAssignee] = useState("");
  const [newTaskBody, setNewTaskBody] = useState("");
  const [taskAssigneeOptions, setTaskAssigneeOptions] = useState<LeadTransferCandidate[]>([]);
  const [taskAssigneeListError, setTaskAssigneeListError] = useState<string | null>(null);
  const [taskAssigneeUseRawUuid, setTaskAssigneeUseRawUuid] = useState(false);
  const [taskAssigneeLoading, setTaskAssigneeLoading] = useState(false);

  useEffect(() => {
    if (tab !== "tasks" || !scopedOrgId) { setTaskAssigneeLoading(false); return; }
    let cancelled = false; setTaskAssigneeListError(null); setTaskAssigneeLoading(true);
    void (async () => { const { data, error } = await fetchLeadTransferCandidates(scopedOrgId); if (cancelled) return; setTaskAssigneeLoading(false); if (error) { setTaskAssigneeListError(error); setTaskAssigneeOptions([]); return; } setTaskAssigneeOptions(data ?? []); })();
    return () => { cancelled = true; };
  }, [tab, scopedOrgId]);

  const loadFastfieldLedger = useCallback(async () => {
    setFastfieldLoading(true);
    const r = await listOpsFastfieldSubmissions();
    setFastfieldLoading(false);
    if (r.ok) setFastfieldSubmissions((((r.data as { submissions?: FastfieldSubmissionLedgerRow[] }).submissions ?? []) as FastfieldSubmissionLedgerRow[]));
  }, []);
  const refreshSummary = useCallback(async () => {
    const r = await getOpsSummary();
    if (r.ok && r.data && typeof r.data === "object") {
      const d = r.data as {
        unread_notifications?: number;
        overdue_paperwork_items?: number;
        open_tasks_for_me?: number;
        unmapped_host_lots?: number;
      };
      setSummary({
        unread: d.unread_notifications ?? 0,
        overdue: d.overdue_paperwork_items ?? 0,
        tasks: d.open_tasks_for_me ?? 0,
        unmappedLots: d.unmapped_host_lots ?? 0,
      });
    }
  }, []);
  const loadDeals = useCallback(async () => {
    setLoading(true); const r = await listOpsDeals({ status: "active", page_size: DEALS_PICKER_PAGE_SIZE }); setLoading(false);
    if (!r.ok) { setMsg(r.error); return; }
    const d = r.data as { deals?: DealRow[] }; setDeals((d.deals ?? []) as DealRow[]); setMsg("");
  }, []);
  const loadDetail = useCallback(async (id: string) => {
    setLoading(true);
    const [dr, er] = await Promise.all([
      getOpsDeal(id, { include: "attachments", signMedia: true }),
      listOpsIntegrationEvents(id),
    ]);
    setLoading(false);
    if (!dr.ok) { setMsg(dr.error); return; }
    const p = dr.data as {
      deal: Record<string, unknown>;
      paperwork_items: PaperItem[];
      tasks: OrgTask[];
      external_ids: unknown[];
      timeline_events?: any[];
      attachments?: OpsPaperworkAttachment[];
      salesperson?: DealDetail["salesperson"];
      deal_type_match?: DealTypeMatchState | null;
      deal_type_catalog?: DealTypeMatchCatalog | null;
      linked_unit?: DealDetail["linked_unit"];
    };
    setDetail({
      deal: p.deal,
      paperwork_items: p.paperwork_items ?? [],
      tasks: p.tasks ?? [],
      external_ids: p.external_ids ?? [],
      timeline_events: p.timeline_events ?? [],
      attachments: p.attachments ?? [],
      salesperson: p.salesperson ?? null,
      deal_type_match: p.deal_type_match ?? null,
      deal_type_catalog: p.deal_type_catalog ?? null,
      linked_unit: p.linked_unit ?? null,
    });
    if (er.ok) setEvents((((er.data as { events?: IntEvent[] }).events ?? []) as IntEvent[])); setMsg("");
  }, []);

  useEffect(() => { void refreshSummary(); }, [refreshSummary]);
  useEffect(() => {
    if (!canOpenDeals) return;
    const pending = consumePendingOpsPaperworkDealId();
    if (!pending) return;
    setTab("deals");
    setSelectedId(pending);
    void loadDeals();
  }, [canOpenDeals, loadDeals]);
  useEffect(() => {
    if (opsPaperworkTabAllowed(tab, { canManage: canManageOps, canReviewDeals })) return;
    setTab(canReviewDeals ? "deals" : "tasks");
  }, [canManageOps, canReviewDeals, tab]);
  useEffect(() => { if (!canManageOps) return; if (tab === "payables") void loadDeals(); }, [canManageOps, tab, loadDeals]);
  useEffect(() => { if (selectedId) void loadDetail(selectedId); }, [selectedId, loadDetail]);
  useEffect(() => {
    if (tab === "tasks") void (async () => { const [i, s] = await Promise.all([listOpsTasks("inbox"), listOpsTasks("sent")]); if (i.ok) setTasksInbox((((i.data as { tasks?: OrgTask[] }).tasks ?? []) as OrgTask[])); if (s.ok) setTasksSent((((s.data as { tasks?: OrgTask[] }).tasks ?? []) as OrgTask[])); })();
    if (tab === "notifications") void (async () => { const r = await listOpsNotifications(); if (r.ok) setNotifications((((r.data as { notifications?: typeof notifications }).notifications ?? []) as typeof notifications)); })();
    if (tab === "settings" && canManageOps) void (async () => {
      const [sr, lr] = await Promise.all([getOpsSettings(), listOpsFastfieldSubmissions()]);
      if (sr.ok) setSettings((sr.data as { settings: Record<string, unknown> }).settings);
      if (lr.ok) setFastfieldSubmissions((((lr.data as { submissions?: FastfieldSubmissionLedgerRow[] }).submissions ?? []) as FastfieldSubmissionLedgerRow[]));
    })();
    if (tab === "timeline" && canOpenDeals) void (async () => { const r = await listOpsIntegrationEvents(selectedId || undefined); if (r.ok) setEvents((((r.data as { events?: IntEvent[] }).events ?? []) as IntEvent[])); })();
  }, [tab, selectedId, canManageOps, canOpenDeals]);

  async function onCreateDeal() { setLoading(true); const r = await createOpsDeal({ display_name: newDealName.trim() || "New deal", seed_paperwork: true }); setLoading(false); if (!r.ok) { setMsg(r.error); return; } setNewDealName(""); await loadDeals(); const deal = (r.data as { deal: { id: string } }).deal; setSelectedId(deal.id); setMsg("Deal created."); }
  async function onSeedTemplates() { setLoading(true); const r = await seedOpsTemplates(); setLoading(false); setMsg(r.ok ? "Default paperwork templates ensured." : r.error); }
  async function onSavePaperRow(row: PaperItem, patch: Record<string, unknown>) { const r = await patchOpsPaperworkItem({ id: row.id, ...patch }); if (!r.ok) setMsg(r.error); else if (selectedId) void loadDetail(selectedId); void refreshSummary(); }
  async function onTaskAction(task: OrgTask, action: string, extra?: Record<string, unknown>) {
    const r = await patchOpsTask({ id: task.id, action, ...(extra ?? {}) }); if (!r.ok) { setMsg(r.error); return; }
    setMsg("Task updated."); const [i, s] = await Promise.all([listOpsTasks("inbox"), listOpsTasks("sent")]); if (i.ok) setTasksInbox((((i.data as { tasks?: OrgTask[] }).tasks ?? []) as OrgTask[])); if (s.ok) setTasksSent((((s.data as { tasks?: OrgTask[] }).tasks ?? []) as OrgTask[])); void refreshSummary();
  }
  async function onCreateTask() {
    if (!newTaskAssignee.trim() || !newTaskTitle.trim()) { setMsg("Assignee user id and title required."); return; }
    const r = await createOpsTask({ assignee_user_id: newTaskAssignee.trim(), title: newTaskTitle.trim(), body: newTaskBody, deal_id: selectedId });
    if (!r.ok) { setMsg(r.error); return; }
    setNewTaskTitle(""); setNewTaskBody(""); setMsg("Task created."); void refreshSummary();
    if (tab === "tasks") { const i = await listOpsTasks("inbox"); if (i.ok) setTasksInbox((((i.data as { tasks?: OrgTask[] }).tasks ?? []) as OrgTask[])); }
  }
  async function onMarkAllNotificationsRead() { await markAllOpsNotificationsRead(); const r = await listOpsNotifications(); if (r.ok) setNotifications((((r.data as { notifications?: typeof notifications }).notifications ?? []) as typeof notifications)); void refreshSummary(); }
  async function onToggleSetting(key: string, value: boolean) {
    const r = await patchOpsSettings({ [key]: value });
    if (!r.ok) {
      setMsg(r.error);
      return;
    }
    setSettings({ ...(settings ?? {}), ...((r.data as { settings: Record<string, unknown> }).settings ?? {}) });
  }
  async function onPatchUrl(key: string, value: string | null) {
    const r = await patchOpsSettings({ [key]: value });
    if (!r.ok) {
      setMsg(r.error);
      return;
    }
    setSettings({ ...(settings ?? {}), ...((r.data as { settings: Record<string, unknown> }).settings ?? {}) });
  }
  async function onRotateInventoryHostWebhookToken() {
    setWebhookTokenBusy(true);
    const r = await rotateOpsInventoryHostWebhookToken();
    setWebhookTokenBusy(false);
    if (!r.ok) { setMsg(r.error); return; }
    setSettings((prev) => ({ ...(prev ?? {}), inventory_host_inbound_webhook_token: r.data.token }));
    setMsg("Inbound webhook token saved. Update pasted URLs that still have the old token.");
  }
  async function onSeedRules() { const r = await seedOpsPaperworkRuleSet(); setMsg(r.ok ? "Default manifest rules saved for org." : r.error); }
  async function onProbeStubs() {
    const [u, d] = await Promise.all([getOpsDealUploadStub(), getOpsOwnershipDigestPreview()]);
    const um = u.ok ? ((u.data as { message?: string }).message ?? "Drive probe OK") : u.error;
    const dm = d.ok
      ? `${(d.data as { preview?: { title?: string } }).preview?.title ?? "digest preview OK"}`
      : d.error;
    setMsg(`Upload stub: ${um}\nDigest preview: ${dm}`);
  }
  async function onFastfieldSync() {
    const r = await postOpsFastfieldSync();
    if (r.ok) {
      const subs = (r.data as { submissions?: FastfieldSubmissionLedgerRow[] }).submissions;
      if (subs) setFastfieldSubmissions(subs);
      setMsg((r.data as { message?: string }).message ?? "OK");
    } else {
      setMsg(r.error);
    }
    await loadFastfieldLedger();
  }
  async function onInviteSalesperson() {
    const sp = detail?.salesperson;
    const token = typeof args.auth?.session?.access_token === "string" ? args.auth.session.access_token : "";
    if (!sp || !selectedId) return;
    if (!token) { setMsg("Not authenticated."); return; }
    setSalespersonBusy(true);
    try {
      await inviteDealSalesperson({ token, salesperson: sp });
      const stamped = await postOpsDealSalesperson({ deal_id: selectedId, action: "mark_invited" });
      if (!stamped.ok) setMsg(stamped.error);
      else setMsg(`Invite sent to ${sp.host.email}.`);
      await loadDetail(selectedId);
    } catch (e: unknown) {
      setMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setSalespersonBusy(false);
    }
  }
  async function onRequestSalespersonInvite() {
    if (!selectedId) return;
    setSalespersonBusy(true);
    const r = await postOpsDealSalesperson({ deal_id: selectedId, action: "request" });
    setSalespersonBusy(false);
    if (!r.ok) { setMsg(r.error); return; }
    setMsg("Invite requested. Admins will see it on this deal.");
    await loadDetail(selectedId);
  }
  async function onAddSalespersonLocation() {
    if (!selectedId) return;
    setSalespersonBusy(true);
    const r = await postOpsDealSalesperson({ deal_id: selectedId, action: "add_location" });
    setSalespersonBusy(false);
    if (!r.ok) { setMsg(r.error); return; }
    setMsg("Salesperson added to this location.");
    await loadDetail(selectedId);
  }
  async function onAssignDealType(productTypeId: string, saleTypeId: string | null) {
    if (!selectedId) return;
    setTypeMatchBusy(true);
    const r = await postOpsDealTypeMatch({
      deal_id: selectedId,
      action: "assign",
      product_type_id: productTypeId,
      sale_type_id: saleTypeId,
    });
    setTypeMatchBusy(false);
    if (!r.ok) { setMsg(r.error); return; }
    setMsg("Deal type saved.");
    await loadDetail(selectedId);
  }
  async function onRematchDealType() {
    if (!selectedId) return;
    setTypeMatchBusy(true);
    const r = await postOpsDealTypeMatch({ deal_id: selectedId, action: "rematch" });
    setTypeMatchBusy(false);
    if (!r.ok) { setMsg(r.error); return; }
    setMsg("Re-matched from the incoming feed.");
    await loadDetail(selectedId);
  }
  const visibleTabs = visibleOpsPaperworkTabs({ canManage: canManageOps, canReviewDeals });

  return {
    tab, setTab, summary, deals, selectedId, setSelectedId, detail, events, tasksInbox, tasksSent, notifications, settings, setSettings,
    msg, setMsg, loading, newDealName, setNewDealName, newTaskTitle, setNewTaskTitle, newTaskAssignee, setNewTaskAssignee, newTaskBody, setNewTaskBody,
    taskAssigneeOptions, taskAssigneeListError, taskAssigneeUseRawUuid, setTaskAssigneeUseRawUuid, taskAssigneeLoading, visibleTabs,
    loadDeals, loadDetail, onCreateDeal, onSeedTemplates, onSavePaperRow, onTaskAction, onCreateTask, onMarkAllNotificationsRead,
    onToggleSetting, onPatchUrl, onRotateInventoryHostWebhookToken, webhookTokenBusy, onSeedRules, onProbeStubs, onFastfieldSync, fastfieldSubmissions, fastfieldLoading,
    salespersonBusy, onInviteSalesperson, onRequestSalespersonInvite, onAddSalespersonLocation,
    typeMatchBusy, onAssignDealType, onRematchDealType, refreshSummary,
  };
}

